import type { Option } from '@/constants/institute';

/**
 * The vocabulary of the attendance **Reports** tab.
 *
 * Lives here rather than in the screen that draws it because three things read
 * it — the filter's options, the threshold a row is coloured against, and the
 * sparkline's stroke — and a percentage repeated across those would be three
 * places to change one rule (CLAUDE.md §3).
 */

/**
 * What the table is narrowed to. A lens on one period's figures, not a
 * different period: the headline tiles stay the whole class's whichever is
 * chosen, so "3 defaulters out of 90.9%" reads the way it is meant to.
 *
 * Applied in the client. The server answers with the section's whole roster
 * and its totals, which is the same request for all three — asking again per
 * lens would be three round trips for one set of rows.
 */
export type AttendanceReportType = 'CLASS_SUMMARY' | 'DEFAULTERS' | 'PERFECT';

/**
 * Below this a child is a defaulter.
 *
 * 75% is the bar Indian schools work to — the figure that decides who is
 * written to and, in many boards, who may sit the examination.
 */
export const DEFAULTER_THRESHOLD = 75;

/**
 * Below this a defaulter is a serious one — the lower edge of the middle band
 * on the "students by attendance" chart, between the safe and the worrying.
 */
export const SERIOUS_DEFAULTER_THRESHOLD = 50;

export const REPORT_TYPE_OPTIONS: Option<AttendanceReportType>[] = [
  { value: 'CLASS_SUMMARY', label: 'Class Summary' },
  { value: 'DEFAULTERS', label: `Defaulters (below ${DEFAULTER_THRESHOLD}%)` },
  { value: 'PERFECT', label: 'Perfect Attendance' },
];

/**
 * The window the screen opens on: this month so far, as the API defaults it.
 *
 * Read in IST, not UTC — before half past five in the morning the two are on
 * different dates, and a register belongs to an institute day.
 */
export function defaultReportPeriod(): { from: string; to: string } {
  // `en-CA` is the one common locale that formats a date as `YYYY-MM-DD`.
  const to = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

  return { from: `${to.slice(0, 7)}-01`, to };
}
